// TÖMEGES KIADÁS-IMPORT — teljes szélességű modál: sablon → fájl → ELŐNÉZET → véglegesítés → jelentés.
//
// MIÉRT MODÁL ÉS NEM A BEÚSZÓ SÁV: az előnézet 14 oszlopos tábla, az a keskeny sávba nem fér be. A gomb a
// lap eszköztárában áll, a „+Új kiadás" előtt.
//
// A SOR-JAVÍTÁS UGYANAZON AZ ŰRLAPON megy, amit a kézi felvitel használ (`ExpenseFormSheet` `draft` módban):
// ettől lesz igaz, hogy az import nem tud mást, mint az űrlap. Egy saját, táblázatos szerkesztő egy második
// validációs felületet jelentene, és garantáltan elcsúszna.
//
// SEMMI nem íródik a véglegesítésig. Blokkoló hibával a véglegesítés tiltott — nem engedjük, hogy a jó sorok
// felmenjenek és a rosszak kimaradjanak: egy félig felvitt bérhónap nehezebben vehető észre, mint egy fel nem
// vitt.

import {
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ExpenseFormSheet } from '@/components/expenses/expense-form-sheet';
import { monthOptions } from '@/components/expenses/expense-month-cell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Text } from '@/components/ui/text';
import { expenseKeys } from '@/features/expenses/api';
import { IMPORT_COLUMNS, readImportFile, type ImportColumn } from '@/features/expenses/import/file';
import { downloadImportTemplate } from '@/features/expenses/import/template';
import {
  expenseFormFromDraft,
  toExpenseBody,
  type ExpenseFormValues,
} from '@/features/expenses/form';
import { api } from '@/lib/api';
import { formatMoney } from '@/lib/format-money';

import type {
  ExpenseCategoryRow,
  ExpenseImportResolvedRow,
  ExpenseWriteBody,
} from '@atlas/contracts';
import type { JSX } from 'react';

export interface ExpenseImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: readonly ExpenseCategoryRow[];
  /** A NÉZETT hónap — a sablon vonatkozási-hónap legördülője erre az ablakra épül (6 vissza, 3 előre). */
  month: string;
  /**
   * A köteg sorainak megnézése a listán (a jelentés „Megnézem" gombja). A HÓNAPOT is átadjuk: a lista egy
   * HÓNAPOT mutat, és a köteg jellemzően nem a nézett hónapba esik — enélkül a „Megnézem" üres listára
   * vinne, és a kezelő azt hinné, semmi nem ment be (mérve, lokálban).
   */
  onShowBatch: (batchId: string, month: string | null) => void;
}

type Stage = 'pick' | 'preview' | 'done';

interface Report {
  batchId: string;
  created: number;
  failed: number;
}

/** A sor állapota a táblán — a jelölés SORRENDJE: hiba > figyelmeztetés > rendben. */
function levelOf(row: ExpenseImportResolvedRow): 'error' | 'warning' | 'ok' {
  if (row.issues.some((i) => i.level === 'error')) return 'error';
  return row.issues.some((i) => i.level === 'warning') ? 'warning' : 'ok';
}

export function ExpenseImportDialog({
  open,
  onOpenChange,
  categories,
  month,
  onShowBatch,
}: ExpenseImportDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<Stage>('pick');
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ExpenseImportResolvedRow[]>([]);
  const [totals, setTotals] = useState<{ currency: string; grossAmount: string }[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  /** A köteg ELSŐ sorának vonatkozási hónapja — ide ugrik a lista a „Megnézem"-re. */
  const [batchMonth, setBatchMonth] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ index: number; values: ExpenseFormValues } | null>(null);

  const headerLabels = Object.fromEntries(
    IMPORT_COLUMNS.map((c) => [c, t(`expenses.import.columns.${c}`)]),
  ) as Record<ImportColumn, string>;

  const errorRows = rows.filter((r) => levelOf(r) === 'error').length;
  const warningRows = rows.filter((r) => levelOf(r) === 'warning').length;
  // A GOMB a TÉNYLEGESEN beírandó sorokat számolja, nem a fájl hosszát: hibás sorból nincs body, és egy
  // „Véglegesítés (3)" felirat 2 beírt sor mellett hazudna.
  const writable = rows.filter((r) => r.body !== null).length;

  const reset = (): void => {
    setStage('pick');
    setRows([]);
    setTotals([]);
    setReport(null);
    setFileName(null);
    setEditing(null);
    setBatchMonth(null);
  };

  const downloadTemplate = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = await api.expenses.importTemplate.query();
      if (res.status !== 200) {
        toast.error(t('toast:expenses.import.templateError'));
        return;
      }
      await downloadImportTemplate(
        res.body,
        {
          labels: headerLabels,
          sheets: {
            rows: t('expenses.import.sheet.rows'),
            values: t('expenses.import.sheet.values'),
          },
          valueHeaders: {
            categories: t('expenses.import.sheet.categories'),
            salary: t('expenses.import.sheet.salary'),
            employees: t('expenses.import.sheet.employees'),
            issuers: t('expenses.import.sheet.issuers'),
            currencies: t('expenses.import.sheet.currencies'),
            noBill: t('expenses.import.sheet.noBill'),
            months: t('expenses.import.sheet.months'),
          },
          // OSZLOPONKÉNTI SÚGÓ: a cellára kattintva jelenik meg. Ez a „vezetőszár" — a kitöltő nem onnan tudja
          // meg a formátumot, hogy a feltöltés után hibát kap.
          guides: Object.fromEntries(
            IMPORT_COLUMNS.map((c) => [
              c,
              {
                title: t(`expenses.import.columns.${c}`),
                prompt: t(`expenses.import.guides.${c}`),
              },
            ]),
          ) as Record<ImportColumn, { title: string; prompt: string }>,
          noBillWords: {
            missing: t('expenses.import.noBill.missing'),
            present: t('expenses.import.noBill.present'),
          },
        },
        // UGYANAZ az ablak, mint a lap sor-cellájában: 6 hónap vissza, 3 előre a NÉZETT hónaptól.
        monthOptions(month),
      );
    } finally {
      setBusy(false);
    }
  };

  /** A kiválasztott fájl beolvasása ÉS azonnali előnézet — a kettő egy mozdulat a kezelőnek. */
  const pickFile = async (file: File): Promise<void> => {
    setBusy(true);
    try {
      const parsed = await readImportFile(file, headerLabels);
      if (parsed.missingHeaders.length > 0) {
        toast.error(
          t('toast:expenses.import.missingColumns', {
            columns: parsed.missingHeaders.map((c) => headerLabels[c]).join(', '),
          }),
        );
        return;
      }
      if (parsed.rows.length === 0) {
        toast.error(t('toast:expenses.import.empty'));
        return;
      }
      await runPreview(parsed.rows.map((r, i) => ({ ...r, rowNumber: i + 1 })));
      setFileName(file.name);
    } catch {
      toast.error(t('toast:expenses.import.unreadable'));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const runPreview = async (raw: { rowNumber: number }[]): Promise<void> => {
    const res = await api.expenses.importPreview.mutation({
      body: { rows: raw as never, lang: locale === 'en' ? 'en' : 'hu' },
    });
    if (res.status !== 200) {
      toast.error(t('toast:expenses.import.previewError'));
      return;
    }
    setRows(res.body.rows);
    setTotals(res.body.totals);
    setStage('preview');
  };

  const commit = async (): Promise<void> => {
    const bodies = rows.map((r) => r.body).filter((b): b is ExpenseWriteBody => b !== null);
    if (bodies.length === 0) return;
    setBusy(true);
    try {
      // A KÖTEG-AZONOSÍTÓT a KLIENS adja, a kérés ELŐTT: ez egyben a commit idempotencia-kulcsa, tehát a
      // dupla kattintás második kérése ugyanazzal az azonosítóval érkezik, és a szerver 409-cel zárja.
      const batchId = crypto.randomUUID();
      const res = await api.expenses.importCommit.mutation({ body: { batchId, rows: bodies } });
      if (res.status === 409) {
        toast.error(t('toast:expenses.import.duplicateBatch'));
        return;
      }
      if (res.status !== 200) {
        toast.error(t('toast:expenses.import.commitError'));
        return;
      }
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      // A VONATKOZÁSI hónap ugyanúgy származik, mint a soron: kézi érték, annak híján a bizonylat dátuma
      // (teljesítés → kiállítás).
      const first = bodies[0];
      setBatchMonth(
        first === undefined
          ? null
          : (first.accountingMonth ?? first.fulfillmentDate ?? first.issueDate).slice(0, 7),
      );
      setReport({
        batchId: res.body.batchId,
        created: res.body.created,
        failed: res.body.failed.length,
      });
      setStage('done');
    } finally {
      setBusy(false);
    }
  };

  const undo = async (): Promise<void> => {
    if (report === null) return;
    setBusy(true);
    try {
      const res = await api.expenses.importUndo.mutation({ params: { batchId: report.batchId } });
      if (res.status !== 200) {
        toast.error(t('toast:expenses.import.undoError'));
        return;
      }
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      toast.success(t('toast:expenses.import.undone', { count: res.body.deleted }));
      reset();
    } finally {
      setBusy(false);
    }
  };

  /** A JAVÍTOTT sor visszaírása. A VÁZLAT is frissül — különben az űrlap újranyitva a régi értékeket hozná. */
  const applyFix = (index: number, values: ExpenseFormValues): void => {
    const body = toExpenseBody(values);
    setRows((prev) =>
      prev.map((r, i) =>
        i === index
          ? // A javított sor leletei eltűnnek: a szerver a véglegesítéskor úgyis újra ellenőriz, és egy
            // „javítottam, de piros maradt" állapot csak zavarna.
            {
              ...r,
              body,
              draft: {
                issuerKey: body.issuerKey,
                supplierName: body.supplierName,
                supplierTaxNumber: body.supplierTaxNumber ?? null,
                noBill: values.noBill,
                invoiceNumber: body.invoiceNumber,
                issueDate: body.issueDate,
                fulfillmentDate: body.fulfillmentDate ?? null,
                dueDate: body.dueDate ?? null,
                netAmount: body.netAmount,
                vatAmount: body.vatAmount,
                currency: body.currency,
                categoryId: body.categoryId ?? null,
                employeeIdentityId: body.employeeIdentityId ?? null,
                accountingMonth: body.accountingMonth ?? null,
                note: body.note ?? null,
              },
              issues: [],
            }
          : r,
      ),
    );
    setEditing(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next) reset();
        }}
      >
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t('expenses.import.title')}</DialogTitle>
            <DialogDescription>{t('expenses.import.description')}</DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            {stage === 'pick' && (
              <div className="space-y-4">
                <Text variant="secondary" as="p" className="text-sm">
                  {t('expenses.import.pickHelp')}
                </Text>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => void downloadTemplate()} disabled={busy}>
                    <ArrowDownTrayIcon className="size-4" />
                    {t('expenses.import.downloadTemplate')}
                  </Button>
                  <Button
                    variant="brand"
                    onClick={() => fileInput.current?.click()}
                    disabled={busy}
                  >
                    {t('expenses.import.chooseFile')}
                  </Button>
                </div>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".xlsx,.csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void pickFile(file);
                  }}
                />
              </div>
            )}

            {stage === 'preview' && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">
                    {t('expenses.import.rowCount', { count: rows.length })}
                  </Badge>
                  {errorRows > 0 && (
                    <Badge variant="destructive">
                      {t('expenses.import.errorCount', { count: errorRows })}
                    </Badge>
                  )}
                  {warningRows > 0 && (
                    <Badge variant="outline">
                      {t('expenses.import.warningCount', { count: warningRows })}
                    </Badge>
                  )}
                  {totals.map((tt) => (
                    <Badge key={tt.currency} variant="secondary" className="tabular-nums">
                      {formatMoney(tt.grossAmount, tt.currency, locale)}
                    </Badge>
                  ))}
                  {fileName !== null && (
                    <Text variant="secondary" as="span" className="text-xs">
                      {fileName}
                    </Text>
                  )}
                </div>

                {/* AZ ELŐNÉZET MINDEN SORT MUTAT, nem csak a hibásakat: a véglegesítés előtt az a kérdés,
                    hogy a FÁJL EGÉSZE stimmel-e. A görgetés a táblán belül marad. */}
                <div className="border-border/60 max-h-[52vh] overflow-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-sunken/60 sticky top-0">
                      <tr className="text-left">
                        <th className="w-10 px-2 py-1.5" />
                        <th className="px-2 py-1.5">{t('expenses.import.columns.supplierName')}</th>
                        <th className="px-2 py-1.5">{t('expenses.import.columns.categoryPath')}</th>
                        <th className="px-2 py-1.5">{t('expenses.import.columns.employeeName')}</th>
                        <th className="px-2 py-1.5 text-right">
                          {t('expenses.import.columns.netAmount')}
                        </th>
                        <th className="w-24 px-2 py-1.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => {
                        const level = levelOf(row);
                        return (
                          /**
                           * EGY SOR = EGY VÍZSZINTES VONAL (user-lelet). A leletek SAJÁT, teljes szélességű
                           * sorba mennek, nem a szállító cellájába: ott megnövelték a cella magasságát, a
                           * többi oszlop (és a sor végi gomb) pedig felülre igazodott — a sor „megtört".
                           *
                           * A két `<tr>` EGY egységként olvasódik: a felső viszi a választóvonalat, az alsó
                           * nem, és a React-kulcs is ugyanabból a sor-számból képződik.
                           */
                          <Fragment key={row.rowNumber}>
                            <tr className="border-border/40 border-t align-middle">
                              <td className="px-2 py-1.5">
                                {level === 'error' ? (
                                  <XCircleIcon className="text-destructive size-4" />
                                ) : level === 'warning' ? (
                                  <ExclamationTriangleIcon className="text-subtle size-4" />
                                ) : (
                                  <CheckCircleIcon className="text-subtle size-4" />
                                )}
                              </td>
                              {/* A VÁZLATBÓL írunk, nem a kész body-ból: a hibás sornak nincs body-ja, és
                                  csupa „—"-ként állna ott — pedig a fájlban minden más érték megvan. */}
                              <td className="truncate px-2 py-1.5">
                                {row.draft.supplierName === '' ? '—' : row.draft.supplierName}
                              </td>
                              <td className="text-subtle px-2 py-1.5 text-xs">
                                {row.categoryLabel ?? '—'}
                              </td>
                              <td className="text-subtle px-2 py-1.5 text-xs">
                                {row.employeeLabel ?? '—'}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums">
                                {row.draft.netAmount === null
                                  ? '—'
                                  : formatMoney(row.draft.netAmount, row.draft.currency, locale)}
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  // A VÁZLATBÓL töltünk, nem a kész body-ból: a HIBÁS sornak nincs body-ja,
                                  // és az üres űrlap elvenné az összes többi, HELYES értéket (ár, kategória,
                                  // munkatárs, megjegyzés) — épp azt, amit a tábla mellette ki is ír.
                                  onClick={() =>
                                    setEditing({ index, values: expenseFormFromDraft(row.draft) })
                                  }
                                >
                                  <PencilSquareIcon className="size-4" />
                                  {t('expenses.import.fix')}
                                </Button>
                              </td>
                            </tr>
                            {row.issues.length > 0 && (
                              <tr>
                                {/* A SZÁLLÍTÓ oszlopa alatt kezdődik (az állapot-ikon hasábját átugorja),
                                    hogy a lelet ahhoz a sorhoz tartozzon, ami fölötte áll. */}
                                <td />
                                <td className="px-2 pb-1.5" colSpan={5}>
                                  <ul className="space-y-0.5">
                                    {row.issues.map((issue, i) => (
                                      <li
                                        key={i}
                                        className={
                                          issue.level === 'error'
                                            ? 'text-destructive text-xs'
                                            : 'text-subtle text-xs'
                                        }
                                      >
                                        {issue.message}
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {errorRows > 0 && (
                  <Text variant="secondary" as="p" className="text-destructive text-xs">
                    {t('expenses.import.blocked')}
                  </Text>
                )}
              </div>
            )}

            {stage === 'done' && report !== null && (
              <div className="space-y-3">
                <Text variant="itemTitle" as="p">
                  {t('expenses.import.doneTitle', { count: report.created })}
                </Text>
                {report.failed > 0 && (
                  <Text variant="secondary" as="p" className="text-destructive text-sm">
                    {t('expenses.import.doneFailed', { count: report.failed })}
                  </Text>
                )}
                <Text variant="secondary" as="p" className="text-sm">
                  {t('expenses.import.doneHelp')}
                </Text>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            {stage === 'preview' && (
              <>
                <Button variant="ghost" onClick={reset} disabled={busy}>
                  {t('expenses.import.another')}
                </Button>
                <Button
                  variant="brand"
                  onClick={() => void commit()}
                  disabled={busy || errorRows > 0 || writable === 0}
                >
                  {t('expenses.import.commit', { count: writable })}
                </Button>
              </>
            )}
            {stage === 'done' && report !== null && (
              <>
                <Button variant="outline" onClick={() => void undo()} disabled={busy}>
                  <ArrowUturnLeftIcon className="size-4" />
                  {t('expenses.import.undo')}
                </Button>
                <Button
                  variant="brand"
                  onClick={() => {
                    onShowBatch(report.batchId, batchMonth);
                    onOpenChange(false);
                  }}
                >
                  {t('expenses.import.showBatch')}
                </Button>
              </>
            )}
            {stage === 'pick' && (
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* A SOR-JAVÍTÁS a KÉZI felvitel űrlapján — ugyanaz a mező-készlet, ugyanaz a validáció. */}
      <ExpenseFormSheet
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) setEditing(null);
        }}
        expense={null}
        categories={categories}
        {...(editing !== null
          ? {
              draft: {
                values: editing.values,
                employeeLabel: rows[editing.index]?.employeeLabel ?? null,
                title: t('expenses.import.fixTitle'),
                description: t('expenses.import.fixDesc', { row: editing.index + 1 }),
                onSubmit: (values: ExpenseFormValues) => applyFix(editing.index, values),
              },
            }
          : {})}
      />
    </>
  );
}
